import React, { useState, useEffect } from 'react';
import AppShell from '../../../components/AppShell';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Button } from '../../../components/ui/button';
import { fetchWithAuth } from '../../../utils/fetchWithAuth';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_API_BASE;

export default function ReplyBotTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [newTemplate, setNewTemplate] = useState({
    name: '',
    language: 'en',
    category: 'utility',
    content: '',
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetchWithAuth(`${API}/api/replybot/templates`);
        const data = await res.json();
        if (data.success) {
          setTemplates(data.templates || []);
        }
      } catch (err) {
        console.error('Failed to load templates:', err);
      }
    };
  
    fetchTemplates();
  }, []);

  const handleChange = (field, value) => {
    setNewTemplate((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateTemplate = async () => {
    const { name, language, category } = newTemplate;
    setLoadingGenerate(true);

    try {
      const res = await fetchWithAuth(`${API}/api/replybot/templates/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, language, category }),
      });

      const data = await res.json();

      setNewTemplate((prev) => ({
        ...prev,
        content: data.message || '',
      }));
    } catch (err) {
      console.error('AI template generation failed:', err);
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleCreateTemplate = async () => {
    const { name, language, category, content } = newTemplate;
    setLoadingSubmit(true);

    try {
      const res = await fetchWithAuth(`${API}/api/replybot/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, language, category, content }),
      });

      const data = await res.json();
      if (data.success) {
        setTemplates((prev) => [
          ...prev,
          {
            ...data.template,
            id: prev.length + 1,
          },
        ]);

        setNewTemplate({
          name: '',
          language: 'en',
          category: 'utility',
          content: '',
        });
      }
    } catch (err) {
      console.error('Template submission failed:', err);
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <AppShell>
      <div className="col-span-12 space-y-6">
        <div className="max-w-4xl mx-auto py-10 px-4 space-y-8">
          <h1 className="text-2xl font-bold">WhatsApp Templates</h1>

          {/* Existing Templates */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-semibold">Your Templates</h2>
              <div className="grid gap-4">
                {templates.map((t) => (
                  <div key={t.id} className="border rounded-md p-4">
                    <div className="font-semibold">{t.name} ({t.language})</div>
                    <div className="text-sm mb-2 flex gap-4">
  <span>
    <strong>Category:</strong> {t.category}
  </span>
  <span>
    <strong>Status:</strong>{' '}
    <span
      className={`capitalize font-semibold ${
        t.status === 'approved'
          ? 'text-green-600'
          : t.status === 'pending'
          ? 'text-yellow-500'
          : 'text-red-500'
      }`}
    >
      {t.status}
    </span>
  </span>
</div>

                    <div className="text-sm whitespace-pre-wrap">{t.content}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Create New Template */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-semibold">Create New Template</h2>

              <Input
                placeholder="Template Name"
                value={newTemplate.name}
                onChange={(e) => handleChange('name', e.target.value)}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Language</label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={newTemplate.language}
                    onChange={(e) => handleChange('language', e.target.value)}
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="ar">Arabic</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm"
                    value={newTemplate.category}
                    onChange={(e) => handleChange('category', e.target.value)}
                  >
                    <option value="utility">Utility</option>
                    <option value="marketing">Marketing</option>
                    <option value="authentication">Authentication</option>
                  </select>
                </div>
              </div>

              <Textarea
                placeholder="Message content (use {{1}}, {{2}} for variables)"
                value={newTemplate.content}
                onChange={(e) => handleChange('content', e.target.value)}
              />

              <div className="space-x-2 flex items-center">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateTemplate}
                  disabled={loadingGenerate || !newTemplate.name || !newTemplate.category}
                >
                  {loadingGenerate ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>✨ Generate with AI</>
                  )}
                </Button>

                <Button onClick={handleCreateTemplate} disabled={loadingSubmit}>
                  {loadingSubmit ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>💾 Save & Submit for Approval</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
